import { Injectable } from "@angular/core";
import { RestService } from "./rest.service";
import { BehaviorSubject, forkJoin, Observable } from "rxjs";
import { CommonService } from "./common.service";
import { UserSubType } from "../../../appconfig";
import { JwtService } from "../../auth/jwtservice";
import { map } from "rxjs/operators";

/**
 * Configuration for generic user assignment loading
 */
export interface UserAssignmentConfig {
    assignmentFor: 'project' | 'site';  // Type of assignment
    entityId: number;                    // Project ID or Site ID
}

/**
 * Result of user assignment loading
 */
export interface UserAssignmentResult {
    availableUsers: any[];
    selectedUserIds: number[];
}

@Injectable()
export class UserService {
    dummyDataUrl: string = '../../../assets/dummydb/';
    public isCustomerData: boolean = false;
    private customerDetails = new BehaviorSubject<any>(null);

    private requiredRolesSubject = new BehaviorSubject<string[]>([]);  // Holds the roles
    requiredRoles$ = this.requiredRolesSubject.asObservable();         // Observable to subscribe to roles

    //Contains information about currently selected user
    public currentUserData = new BehaviorSubject<any>(null);

    constructor(private restService: RestService, private commonService: CommonService, private jwtService: JwtService) { }

    getUserData() {
        return this.currentUserData.asObservable();
    }

    setUserData(updatedData: any) {
        this.currentUserData.next(updatedData);
    }

    /**
   * Fetches the booking payment details
   * @param siteId (optional) site Id for which user's details needs to be fetched
   * @param projectId (optional) project Id for which user's details needs to be fetched
   * @param userSubTypeId (optional) user Sub Type Id for which user's details needs to be fetched
   * @param userId (optional) customer Id for which user's details needs to be fetched
   * @returns returns the details of user
   */
    getFilterUser(siteId: any = null, projectId: any = null, subTypeId: any = null, userId: any = null, explorationId: any = null) {
        let localUrl: any = 'user';
        const params = [];
        if (siteId) params.push(`siteId=${siteId}`);
        if (projectId) params.push(`projectId=${projectId}`);
        if (subTypeId) params.push(`subTypeId=${subTypeId}`);
        if (userId) params.push(`id=${userId}`);
        if (explorationId) params.push(`explorationId=${explorationId}`);
        
        if (params.length > 0) {
            localUrl += '?' + params.join('&');
        }
        return this.restService.getData(localUrl);
    }


    /**
* Fetches the User's Basic details
* @param requestData request object for which user's details needs to be fetched
* @returns returns the details of user
*/
    getFilterBasicUser(requestData: any) {
        let localUrl: any = 'user/basic';
        //Check If any of the filter is applied
        return this.restService.postData(localUrl, requestData);
    }



    /**
     * Generic method to load user assignments for any entity (project, site, etc.)
     * Loads both available users and pre-selected assigned users in parallel
     * 
     * @param config Configuration with assignmentFor ('project' | 'site') and entityId
     * @returns Observable with availableUsers array and selectedUserIds array
     */
    loadUserAssignments(config: UserAssignmentConfig): Observable<UserAssignmentResult> {
        const availableUsersSource = this.getFilterUser(null, null, UserSubType.User, null);

        let assignedUsersSource: Observable<any>;

        if (config.assignmentFor === 'project') {
            assignedUsersSource = this.restService.getData(`project/${config.entityId}/project-users`);
        } else if (config.assignmentFor === 'site') {
            assignedUsersSource = this.restService.getData(`site/${config.entityId}/site-users`);
        } else {
            throw new Error(`Unknown assignment type: ${config.assignmentFor}`);
        }

        return forkJoin({
            available: availableUsersSource,
            assigned: assignedUsersSource
        }).pipe(
            map(({ available, assigned }) => {
                const availableUsers = available?.result || available || [];
                const assignedUsers = assigned?.result || assigned || [];
                const selectedUserIds = assignedUsers.map((u: any) => u.id);

                return {
                    availableUsers,
                    selectedUserIds
                };
            })
        );
    }


    /**
     * Gets the user's user name (login) based on user id
     * @param userId user Id whose user name has to be fetched
     * @returns returns user's user name
     */
    getUsersUserName(userId: number) {
        let localUrl: any = `user/username?userId=${userId}`;
        return this.restService.getData(localUrl);
    }


    /**
     * Get Bank Data
     * @returns Get API
     */
    getBankData() {
        return this.restService.getData('Bank/')
    }

    /**
     * Post Bank Data
     * @returns Post API
     */
    postBankData(data: any) {
        return this.restService.postData('bank-master/bankaccounts', data)
    }


    /**
     * Update Bank Data
     * @returns Update API
     */
    updateBankData(id: any, data: any) {
        return this.restService.updateData('bank-master/bankaccounts', id, data)
    }

    // Customer Data Refresh As Observable Through
    getCustomerDetail() {
        return this.customerDetails?.asObservable()
    }

    setCustomerDetails(data: any) {
        return this.customerDetails?.next(data);
    }

    getBloodGroupList() {
        return this.commonService.setDataUrl('bloodGroup', '');
    }


    /**
     * Gets user's sites based on user id
     * @param userId User whose sites has to be fetched
     * @returns returns user's sites
     */
    getUserSItes(userId: any) {
        let localUrl: any = 'site?userId=' + userId;
        return this.restService.getData(localUrl);
    }

    /**
     * Gets user's projects based on user id
     * @param userId User whose projects has to be fetched
     * @returns returns user's projects
     */
    getUserProjects(userId: any) {
        let localUrl: any = 'project?userId=' + userId;
        return this.restService.getData(localUrl);
    }

    updateUser(id: any, data: any) {
        return this.restService.updateData('user', id, data)
    }

    setRequiredRoles(roles: string[]): void {
        this.requiredRolesSubject.next(roles);  // Set the roles when they are updated
    }

    getRequiredRoles(): string[] {
        return this.requiredRolesSubject.getValue();  // Get the current roles
    }

    getUserPermissions(): string[] {
        return this.jwtService.currentUserRolesValue || [];
    }

    getUserInfobyURLsource(source: any) {
        // Normalize source to match enum keys
        const normalizedSource = String(source).toLowerCase();

        // Find matching key in enum (case-insensitive)
        const matchedKey = Object.keys(UserSubType).find(
            key => isNaN(Number(key)) && key.toLowerCase() === normalizedSource
        );

        if (matchedKey) {
            const enumValue = UserSubType[matchedKey as keyof typeof UserSubType];
            return {
                id: enumValue,
                label: matchedKey,
            };
        }

        return null;
    }


    /**
* Fetches the booking payment details
* @param siteId (optional) site Id for which user's details needs to be fetched
* @param projectId (optional) project Id for which user's details needs to be fetched
* @param userSubTypeId (optional) user Sub Type Id for which user's details needs to be fetched
* @param userId (optional) customer Id for which user's details needs to be fetched
* @returns returns the details of user
*/
    filterUserLogs(userName: any = null, activity: any = null, action: any = null, dateFrom: any = null) {
        let localUrl: any = 'logs';
        //Check If any of the filter is applied
        if (userName || activity || action || dateFrom) localUrl += '?'
        //Set all filters params if available

        localUrl = userName ? localUrl + 'username=' + userName + '&' : localUrl
        localUrl = activity ? localUrl + 'activity=' + activity + '&' : localUrl
        localUrl = action ? localUrl + 'action=' + action + '&' : localUrl
        localUrl = dateFrom ? localUrl + 'dateFrom=' + dateFrom + '&' : localUrl
        localUrl = localUrl.endsWith("&") ? localUrl.slice(0, -1) : localUrl;
        return this.restService.getData(localUrl);
    }


    /**
     * Returns the user group of currently logged in user from local storage
     * @returns 
     */
    getUserGroup() {
        let userGroup: any = this.commonService.getValueFromLocalStorage('group');
        if (userGroup != null && userGroup != undefined && userGroup != "undefined") {
            return JSON.parse(userGroup).name;
        }
        else {
            return null;
        }
    }
}